import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";

export type VerificationQueueStatus = "pending" | "approved" | "rejected";

export type VerificationEvidence = {
  label: string;
  value: string;
};

export type VerificationApplicant = {
  userId: string | null;
  displayName: string;
  username: string | null;
  email: string | null;
  avatarURL: string | null;
};

export type VerificationQueueRow = {
  id: string;
  requestId: string;
  userId: string | null;
  applicant: VerificationApplicant;
  role: string | null;
  verificationType: string | null;
  evidence: VerificationEvidence[];
  status: VerificationQueueStatus;
  submittedAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  reviewedBy: string | null;
  reviewedByEmail: string | null;
  reviewedAt: Date | null;
  rejectionReason: string | null;
  notes: string | null;
};

export type VerificationQueueData = {
  status: VerificationQueueStatus;
  requests: VerificationQueueRow[];
  fetchedAt: Date;
};

type RawApplicant = {
  userId?: unknown;
  displayName?: unknown;
  username?: unknown;
  email?: unknown;
  avatarURL?: unknown;
};

type RawVerificationRow = {
  id?: unknown;
  requestId?: unknown;
  userId?: unknown;
  applicant?: RawApplicant;
  role?: unknown;
  verificationType?: unknown;
  evidence?: unknown;
  status?: unknown;
  submittedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  reviewedBy?: unknown;
  reviewedByEmail?: unknown;
  reviewedAt?: unknown;
  rejectionReason?: unknown;
  notes?: unknown;
};

type RawQueueResponse = {
  status?: unknown;
  requests?: unknown;
  fetchedAt?: unknown;
};

type RawReviewResponse = {
  request?: RawVerificationRow;
};

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function statusValue(value: unknown): VerificationQueueStatus {
  return value === "approved" || value === "rejected" ? value : "pending";
}

function dateValue(value: unknown): Date | null {
  const raw = stringValue(value);
  if (!raw) {
    return null;
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function evidenceValue(value: unknown): VerificationEvidence[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const label = stringValue(record.label);
    const evidence = stringValue(record.value);
    return label && evidence ? [{ label, value: evidence }] : [];
  });
}

function rowValue(raw: RawVerificationRow): VerificationQueueRow {
  const applicant = raw.applicant ?? {};
  const id = stringValue(raw.id) ?? stringValue(raw.requestId) ?? "unknown_request";

  return {
    id,
    requestId: stringValue(raw.requestId) ?? id,
    userId: stringValue(raw.userId),
    applicant: {
      userId: stringValue(applicant.userId),
      displayName: stringValue(applicant.displayName) ?? stringValue(raw.userId) ?? "Unknown applicant",
      username: stringValue(applicant.username),
      email: stringValue(applicant.email),
      avatarURL: stringValue(applicant.avatarURL)
    },
    role: stringValue(raw.role),
    verificationType: stringValue(raw.verificationType),
    evidence: evidenceValue(raw.evidence),
    status: statusValue(raw.status),
    submittedAt: dateValue(raw.submittedAt),
    createdAt: dateValue(raw.createdAt),
    updatedAt: dateValue(raw.updatedAt),
    reviewedBy: stringValue(raw.reviewedBy),
    reviewedByEmail: stringValue(raw.reviewedByEmail),
    reviewedAt: dateValue(raw.reviewedAt),
    rejectionReason: stringValue(raw.rejectionReason),
    notes: stringValue(raw.notes)
  };
}

export async function fetchVerificationQueue(status: VerificationQueueStatus): Promise<VerificationQueueData> {
  const callable = httpsCallable<{ status: VerificationQueueStatus; limit: number }, RawQueueResponse>(functions, "fetchVerificationQueue");
  const result = await callable({ status, limit: 100 });
  const raw = result.data;

  return {
    status: statusValue(raw.status),
    requests: Array.isArray(raw.requests) ? raw.requests.map((row) => rowValue(row as RawVerificationRow)) : [],
    fetchedAt: dateValue(raw.fetchedAt) ?? new Date()
  };
}

export async function reviewVerificationRequest({
  notes,
  rejectionReason,
  requestId,
  status
}: {
  notes: string;
  rejectionReason?: string;
  requestId: string;
  status: Exclude<VerificationQueueStatus, "pending">;
}): Promise<VerificationQueueRow> {
  const callable = httpsCallable<
    { requestId: string; status: Exclude<VerificationQueueStatus, "pending">; notes: string; rejectionReason?: string },
    RawReviewResponse
  >(functions, "reviewVerificationRequest");
  const result = await callable({
    requestId,
    status,
    notes,
    ...(status === "rejected" ? { rejectionReason: rejectionReason ?? "" } : {})
  });

  return rowValue(result.data.request ?? { id: requestId, requestId, status });
}
